import { useEffect, useState } from "react";
import { Moon, Play, Pause, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const SOUNDS = [
  { id: "white", label: "Rumore bianco", hint: "il classico, copre i rumori di casa" },
  { id: "pink", label: "Pioggia leggera", hint: "più morbido e rilassante" },
  { id: "brown", label: "Come nel pancione", hint: "profondo, ricorda il ventre della mamma" },
  { id: "heart", label: "Il battito della mamma", hint: "un cuore calmo, come da dentro la pancia" },
  { id: "brahms", label: "Ninna nanna di Brahms", hint: "la ninna nanna più dolce, con un carillon" },
  { id: "stellina", label: "Brilla brilla la stellina", hint: "carillon lento e delicato" },
  { id: "ninnaoh", label: "Ninna nanna, ninna oh", hint: "la ninna nanna italiana di sempre, al carillon" },
  { id: "waves", label: "Onde del mare", hint: "il respiro lento delle onde sulla riva" },
];

// melodies as [midi note, beats]; 0 = rest
const MELODIES = {
  brahms: { bpm: 66, notes: [
    [64, .5], [64, .5], [67, 2], [64, .5], [64, .5], [67, 2], [64, .5], [67, .5], [72, 1], [71, 1.5], [69, .5], [69, 1], [67, 2],
    [62, .5], [64, .5], [65, 1], [62, 1], [62, .5], [64, .5], [65, 2], [62, .5], [65, .5], [71, .5], [69, .5], [67, 1], [71, 1], [72, 2],
    [60, .5], [60, .5], [72, 2], [69, .5], [65, .5], [67, 2], [64, .5], [60, .5], [65, 1], [67, 1], [69, 1], [67, 2],
    [60, .5], [60, .5], [72, 2], [69, .5], [65, .5], [67, 2], [64, .5], [60, .5], [65, 1], [64, 1], [62, 1], [60, 3], [0, 2]] },
  stellina: { bpm: 60, notes: [
    [60, 1], [60, 1], [67, 1], [67, 1], [69, 1], [69, 1], [67, 2], [65, 1], [65, 1], [64, 1], [64, 1], [62, 1], [62, 1], [60, 2],
    [67, 1], [67, 1], [65, 1], [65, 1], [64, 1], [64, 1], [62, 2], [67, 1], [67, 1], [65, 1], [65, 1], [64, 1], [64, 1], [62, 2],
    [60, 1], [60, 1], [67, 1], [67, 1], [69, 1], [69, 1], [67, 2], [65, 1], [65, 1], [64, 1], [64, 1], [62, 1], [62, 1], [60, 2], [0, 2]] },
  ninnaoh: { bpm: 72, notes: [
    [67, 1], [64, 1], [67, 1], [64, 1], [65, 1], [62, 1], [64, 2], [0, 1],
    [64, 1], [65, 1], [67, 1], [67, 1], [69, 1], [67, 1], [65, 1], [64, 2], [0, 1],
    [67, 1], [67, 1], [69, 1], [67, 1], [65, 1], [64, 1], [62, 1], [64, 2], [0, 1],
    [62, 1], [64, 1], [65, 1], [64, 1], [62, 1], [60, 1], [62, 1], [64, 1], [62, 1], [60, 3], [0, 2]] },
};

const waves = (ctx) => {
  const len = ctx.sampleRate * 20;
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  let last = 0, lp = 0;
  for (let i = 0; i < len; i++) {
    const t = i / ctx.sampleRate;
    last = (last + 0.02 * (Math.random() * 2 - 1)) / 1.02;
    lp += 0.08 * (Math.random() * 2 - 1 - lp);
    const swell = (0.5 - 0.5 * Math.cos((2 * Math.PI * t) / 10)) ** 2;
    d[i] = last * 2.2 * (0.35 + 0.65 * swell) + lp * 0.5 * swell;
  }
  return buf;
};

const musicBox = (ctx, { bpm, notes }) => {
  const beat = 60 / bpm;
  const total = notes.reduce((t, [, b]) => t + b, 0) * beat + 2;
  const buf = ctx.createBuffer(1, Math.ceil(total * ctx.sampleRate), ctx.sampleRate);
  const d = buf.getChannelData(0);
  let t = 0;
  for (const [midi, beats] of notes) {
    if (midi) {
      const f = 440 * 2 ** ((midi - 69) / 12);
      const start = Math.floor(t * ctx.sampleRate);
      const len = Math.min(d.length - start, Math.floor(2.2 * ctx.sampleRate));
      for (let i = 0; i < len; i++) {
        const x = i / ctx.sampleRate;
        const env = Math.min(1, x / 0.006) * Math.exp(-x * 2.2);
        d[start + i] += env * 0.22 * (Math.sin(2 * Math.PI * f * x) + 0.25 * Math.sin(4 * Math.PI * f * x)
          + 0.08 * Math.sin(6 * Math.PI * f * x));
      }
    }
    t += beats * beat;
  }
  return buf;
};

const heartbeat = (ctx) => {
  const period = 60 / 66;
  const beats = 8;
  const buf = ctx.createBuffer(1, Math.ceil(period * beats * ctx.sampleRate), ctx.sampleRate);
  const d = buf.getChannelData(0);
  const thump = (at, f, amp) => {
    const start = Math.floor(at * ctx.sampleRate);
    const len = Math.floor(0.22 * ctx.sampleRate);
    for (let i = 0; i < len && start + i < d.length; i++) {
      const x = i / ctx.sampleRate;
      const env = Math.min(1, x / 0.012) * Math.exp(-x * 22);
      d[start + i] += amp * env * Math.sin(2 * Math.PI * (f - 15 * x) * x);
    }
  };
  for (let b = 0; b < beats; b++) {
    thump(b * period, 58, 0.9);
    thump(b * period + 0.3, 48, 0.6);
  }
  let last = 0;
  for (let i = 0; i < d.length; i++) { last = (last + 0.02 * (Math.random() * 2 - 1)) / 1.02; d[i] += last * 0.6; }
  return buf;
};
const TIMERS = [[15, "15 min"], [30, "30 min"], [60, "1 ora"], [0, "Sempre"]];

// one shared player for the whole app, so the sound keeps playing while navigating
const player = { ctx: null, src: null, gain: null, sound: null, timer: null, listeners: new Set() };
const notify = () => player.listeners.forEach((fn) => fn());

const noiseBuffer = (ctx, type) => {
  if (MELODIES[type]) return musicBox(ctx, MELODIES[type]);
  if (type === "heart") return heartbeat(ctx);
  if (type === "waves") return waves(ctx);
  const len = ctx.sampleRate * 4;
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0, last = 0;
  for (let i = 0; i < len; i++) {
    const w = Math.random() * 2 - 1;
    if (type === "pink") {
      b0 = 0.99886 * b0 + w * 0.0555179; b1 = 0.99332 * b1 + w * 0.0750759; b2 = 0.969 * b2 + w * 0.153852;
      b3 = 0.8665 * b3 + w * 0.3104856; b4 = 0.55 * b4 + w * 0.5329522; b5 = -0.7616 * b5 - w * 0.016898;
      d[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11; b6 = w * 0.115926;
    } else if (type === "brown") {
      last = (last + 0.02 * w) / 1.02; d[i] = last * 3.5;
    } else d[i] = w * 0.5;
  }
  return buf;
};

const stop = () => {
  clearTimeout(player.timer);
  if (player.src && player.gain && player.ctx) {
    const src = player.src;
    player.gain.gain.setTargetAtTime(0, player.ctx.currentTime, 0.4);
    setTimeout(() => { try { src.stop(); } catch { /* already stopped */ } }, 1500);
  }
  player.src = null; player.sound = null; player.endsAt = null;
  notify();
};

const play = (sound, volume, minutes) => {
  if (player.src) { try { player.src.stop(); } catch { /* noop */ } }
  player.ctx = player.ctx || new (window.AudioContext || window.webkitAudioContext)();
  player.ctx.resume();
  player.gain = player.gain || player.ctx.createGain();
  player.gain.connect(player.ctx.destination);
  player.gain.gain.setValueAtTime(0, player.ctx.currentTime);
  player.gain.gain.setTargetAtTime(volume, player.ctx.currentTime, 0.5);
  const src = player.ctx.createBufferSource();
  src.buffer = noiseBuffer(player.ctx, sound);
  src.loop = true;
  src.connect(player.gain);
  src.start();
  player.src = src; player.sound = sound;
  clearTimeout(player.timer);
  player.endsAt = minutes ? Date.now() + minutes * 60000 : null;
  if (minutes) player.timer = setTimeout(stop, minutes * 60000);
  notify();
};

export const NoisePlayer = ({ label, testid = "noise-open" }) => {
  const [, force] = useState(0);
  const [sound, setSound] = useState("white");
  const [volume, setVolume] = useState(0.5);
  const [minutes, setMinutes] = useState(30);
  useEffect(() => {
    const fn = () => force((n) => n + 1);
    player.listeners.add(fn);
    return () => player.listeners.delete(fn);
  }, []);
  const playing = !!player.sound;
  const changeVolume = (v) => {
    setVolume(v);
    if (player.gain && player.ctx) player.gain.gain.setTargetAtTime(v, player.ctx.currentTime, 0.1);
  };
  const choose = (id) => { setSound(id); if (playing) play(id, volume, minutes); };
  const chooseTimer = (m) => { setMinutes(m); if (playing) play(player.sound, volume, m); };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={testid} aria-label="Rumori bianchi per la nanna"
          className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full border text-sm transition-colors ${playing ? "bg-[#8E7BA3] border-[#8E7BA3] text-white" : "border-[#E4DAEC] text-[#7A6890] hover:bg-[#F3EEF6]"}`}>
          <Moon size={15} className={playing ? "animate-pulse" : ""} />
          <span className={label ? "" : "hidden sm:inline"}>{playing ? "In riproduzione" : label || "Rumori bianchi"}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="center" sideOffset={8} collisionPadding={16} data-testid="noise-panel" className="w-[300px] max-w-[calc(100vw-32px)] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto z-50 rounded-2xl border-[#E4DAEC] bg-[#FBF9FC] p-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-serif text-lg text-[#2D2825]">Suoni per la nanna</div>
            <div className="text-[12px] text-[#8C7F78]">Un sottofondo dolce che aiuta il tuo bimbo a calmarsi.</div>
          </div>
          {playing && <button data-testid="noise-stop" onClick={stop} aria-label="Ferma" className="text-[#8C7F78] hover:text-[#2D2825]"><X size={16} /></button>}
        </div>
        <div className="mt-3 space-y-1 max-h-[38vh] overflow-y-auto pr-1">
          {SOUNDS.map((s) => (
            <button key={s.id} data-testid={`noise-sound-${s.id}`} onClick={() => choose(s.id)}
              className={`w-full text-left rounded-xl border px-3 py-2 transition-colors ${sound === s.id ? "border-[#8E7BA3] bg-white" : "border-transparent hover:bg-white"}`}>
              <div className="text-sm text-[#2D2825]">{s.label}</div>
              <div className="text-[11px] text-[#8C7F78]">{s.hint}</div>
            </button>
          ))}
        </div>
        <div className="mt-3 text-[11px] uppercase tracking-widest text-[#8C7F78]">Si spegne da solo dopo</div>
        <div className="mt-1.5 grid grid-cols-4 gap-1.5">
          {TIMERS.map(([m, l]) => (
            <button key={m} data-testid={`noise-timer-${m}`} onClick={() => chooseTimer(m)}
              className={`rounded-full text-[12px] py-1.5 border transition-colors ${minutes === m ? "bg-[#8E7BA3] text-white border-[#8E7BA3]" : "bg-white text-[#5C534E] border-[#E4DAEC]"}`}>{l}</button>
          ))}
        </div>
        <label className="mt-3 block text-[11px] uppercase tracking-widest text-[#8C7F78]">Volume</label>
        <input data-testid="noise-volume" type="range" min={0.05} max={1} step={0.05} value={volume}
          onChange={(e) => changeVolume(Number(e.target.value))} className="w-full accent-[#8E7BA3]" />
        <button data-testid="noise-toggle" onClick={() => (playing ? stop() : play(sound, volume, minutes))}
          className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#8E7BA3] hover:bg-[#7A6890] text-white h-11 text-sm transition-colors">
          {playing ? <><Pause size={16} /> Ferma</> : <><Play size={16} /> Avvia</>}
        </button>
        <p className="mt-2 text-[11px] leading-snug text-[#8C7F78]">Tieni il telefono lontano dalla culla e il volume basso, come una doccia lontana. Su iPhone togli la modalità silenzioso.</p>
      </PopoverContent>
    </Popover>
  );
};
